/**
 * MetricSelector Component
 * Multi-select dropdown for choosing metrics with select all/clear all functionality
 *
 * @module MetricDataDevTool/components/MetricSelector
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Button,
  FormHelperText,
  Chip
} from '@mui/material';
import { getMetricDisplayName } from '../utils/metricDefinitions';
import { METRIC_NAMES } from '../constants';

/**
 * MetricSelector component
 * @param {Object} props - Component props
 * @param {string[]} props.selectedMetrics - Array of selected metric names
 * @param {Function} props.onChange - Callback when selection changes
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled
 * @param {string} [props.error] - Error message to display
 * @param {string} [props.label='Select Metrics'] - Label for the select
 */
export default function MetricSelector({
  selectedMetrics = [],
  onChange,
  disabled = false,
  error = null,
  label = 'Select Metrics'
}) {
  const handleChange = (event) => {
    const value = event.target.value;
    onChange(value);
  };

  const handleSelectAll = () => {
    onChange(METRIC_NAMES);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const sortedMetrics = [...METRIC_NAMES].sort((a, b) =>
    getMetricDisplayName(a).localeCompare(getMetricDisplayName(b))
  );

  return (
    <FormControl fullWidth error={!!error} disabled={disabled}>
      <InputLabel id="metric-selector-label" sx={{ color: '#7C3AED' }}>{label}</InputLabel>
      <Select
        labelId="metric-selector-label"
        id="metric-selector"
        multiple
        value={selectedMetrics}
        onChange={handleChange}
        label={label}
        sx={{
          height: '40px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#7C3AED',
            borderWidth: '2px'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#6D28D9'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#7C3AED'
          }
        }}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.length === METRIC_NAMES.length ? (
              <Chip label="All Metrics" size="small" />
            ) : (
              `${selected.length} selected`
            )}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400
            }
          }
        }}
      >
        {/* Select All / Clear All buttons */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={disabled}
            variant="text"
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={handleClearAll}
            disabled={disabled}
            variant="text"
          >
            Clear All
          </Button>
        </Box>

        {/* Metric options */}
        {sortedMetrics.map((metricName) => (
          <MenuItem key={metricName} value={metricName}>
            <Checkbox checked={selectedMetrics.indexOf(metricName) > -1} />
            <ListItemText primary={getMetricDisplayName(metricName)} />
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}
